/**
 * The Conversations view's URL vocabulary (TRA-1397): the same param names
 * as the Requests view and the Trajectories page, so a reader who learned
 * one listing can read a link to the other, and switching the view keeps
 * the window, the search and the filters. `range`, `q`, `sort` and `page`
 * go through the shared listing hook; this file holds what is the
 * conversations list's own: which columns sort, the metadata dropdowns
 * and how the URL becomes the api's query.
 *
 * Kept free of React so the decisions run in the node vitest setup.
 */

import type {
  ConversationListItemResponse,
  ListConversationsApiTracesConversationsGetParams,
  ListConversationsApiTracesConversationsGetSort,
} from '@/api/generated/model'
import { clientMetadataFilters } from '@/components/listing/clientMetadata'
import {
  metadataQuery,
  type MetadataFilterSpec,
  type MetadataParam,
  type SortState,
} from '@/components/listing/listingParams'
import { REQUEST_PAGE_SIZE } from '@/components/models/home/requestLogOptions'

export type ConversationSortField = ListConversationsApiTracesConversationsGetSort

/** Newest activity first, like the Requests view. */
export const DEFAULT_CONVERSATION_SORT: SortState<ConversationSortField> = {
  field: 'last',
  desc: true,
}

/** The columns that sort: Preview (`run`), Turns and Last active. The
    table shows no Model column, so `sort=model.asc` reads as the default
    on both surfaces. */
export const CONVERSATION_SORT_FIELDS: ConversationSortField[] = ['last', 'turns', 'run']

/** The Model dropdown exists on the team-wide list only: a model page IS
    one model. A conversation can call several models, so each contributes
    all of them. */
export function conversationMetadataFilters(
  teamWide: boolean,
): MetadataFilterSpec<ConversationListItemResponse>[] {
  const client = clientMetadataFilters<ConversationListItemResponse>()
  if (!teamWide) return client
  return [
    {
      param: 'model',
      label: 'Model',
      values: (i) => i.models.map((slug) => ({ value: slug, label: slug })),
    },
    ...client,
  ]
}

export function conversationMetadataParams(teamWide: boolean): MetadataParam[] {
  return conversationMetadataFilters(teamWide).map((f) => f.param)
}

export interface ConversationQueryInput {
  searchParams: URLSearchParams
  /** All time for an id search; otherwise the picked window. */
  range: NonNullable<ListConversationsApiTracesConversationsGetParams['range']>
  /** Trimmed and debounced. */
  search: string
  sort: SortState<ConversationSortField>
  /** 1-based. */
  page: number
  /** The model page's platform Model UUID; undefined on the dashboard. */
  modelId: string | undefined
}

/**
 * The api query for one address bar. The URL's `model` is a gateway slug
 * and goes out as `model_slug`; the model page's UUID goes out as `model`,
 * and on that page the URL's `model` is ignored.
 */
export function conversationQuery({
  searchParams,
  range,
  search,
  sort,
  page,
  modelId,
}: ConversationQueryInput): ListConversationsApiTracesConversationsGetParams {
  const teamWide = modelId === undefined
  const { model, ...client } = metadataQuery(searchParams, conversationMetadataParams(teamWide))
  return {
    range,
    ...(search !== '' ? { q: search } : {}),
    ...(teamWide ? {} : { model: modelId }),
    ...(model === undefined ? {} : { model_slug: model }),
    ...client,
    sort: sort.field,
    dir: sort.desc ? 'desc' : 'asc',
    limit: REQUEST_PAGE_SIZE,
    offset: (page - 1) * REQUEST_PAGE_SIZE,
  }
}
